"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactFlicking = _interopRequireWildcard(require("@egjs/react-flicking"));
var _flickingPlugins = require("@egjs/flicking-plugins");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const parseChildren = children => {
  var _list;
  let list = [];
  if (Array.isArray(children)) {
    list = children;
  } else if ( /*#__PURE__*/_react.default.isValidElement(children)) {
    list.push(children);
  }
  /* In case of using Array.map to render Tabs (Array.flat(); unsupported by IE) */
  if ((_list = list) != null && _list.length) list = list.reduce((acc, val) => acc.concat(val), []);
  return list;
};

/**
 * The Carousel component is used to display a set of elements that we can slide, fade or move in some way.
 */
const Carousel = _ref => {
  let {
    arrows,
    pagination,
    paginationType,
    className,
    children,
    ...attributes
  } = _ref;
  const containerId = (0, _react.useId)();
  const [holding, setHolding] = (0, _react.useState)(false);
  const classes = (0, _clsx.default)('uk-carousel', holding && 'uk-carousel--holding', arrows && 'uk-carousel--with-arrows', pagination && 'uk-carousel--with-pagination', className);
  const plugins = (0, _react.useMemo)(() => {
    let plugins = [];
    if (arrows) plugins.push(new _flickingPlugins.Arrow());
    if (pagination) plugins.push(new _flickingPlugins.Pagination({
      type: paginationType
    }));
    return plugins;
  }, [arrows, pagination, paginationType]);
  const list = parseChildren(children);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: classes
  }, /*#__PURE__*/_react.default.createElement(_reactFlicking.default, _extends({
    onHoldStart: () => setHolding(true),
    onHoldEnd: () => setHolding(false),
    plugins: plugins
  }, attributes), list.map((e, idx) => /*#__PURE__*/_react.default.createElement("div", {
    key: containerId + "-" + idx
  }, e)), /*#__PURE__*/_react.default.createElement(_reactFlicking.ViewportSlot, null, arrows && /*#__PURE__*/_react.default.createElement("span", {
    className: "flicking-arrow-prev"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    name: "angle-left"
  })), arrows && /*#__PURE__*/_react.default.createElement("span", {
    className: "flicking-arrow-next"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    name: "angle-right"
  })), pagination && /*#__PURE__*/_react.default.createElement("div", {
    className: "flicking-pagination"
  }))));
};
Carousel.propTypes = {
  /**
   * Custom CSS class for the main container
   */
  className: _propTypes.default.string,
  /**
   * Display arrows to navigate across panels
   */
  arrows: _propTypes.default.bool,
  /**
   * Display pagination to navigate across panels
   */
  pagination: _propTypes.default.bool,
  /**
   * Type of pagination
   */
  paginationType: _propTypes.default.oneOf(['bullet', 'fraction', 'scroll']),
  // *** Flicking props ***
  // for more details: https://naver.github.io/egjs-flicking/docs/api/Flicking

  // --- UI / Layout ---
  /**
   * Align position of the panels within viewport
   * - Literal strings: "prev", "center", "next",
   * - % values, applied to both panel & camera: "0%", "25%", "42%",
   * - px values, arithmetic calculation with (+/-) is also allowed: "0px", "100px", "50% - 25px",
   * - numbers, same to number + px ("0px", "100px"): 0, 100, 1000,
   * - Setting a different value for panel & camera: { panel: "10%", camera: "25%" }
   */
  align: _propTypes.default.oneOfType([_propTypes.default.oneOf(['prev', 'center', 'next']), _propTypes.default.string, _propTypes.default.number, _propTypes.default.shape({
    panel: _propTypes.default.string,
    camera: _propTypes.default.string
  })]),
  /**
   * Index of the panel to move when Flicking's init is called.
   */
  defaultIndex: _propTypes.default.number,
  /**
   * Direction of panel movement (true: horizontal, false: vertical)
   */
  horizontal: _propTypes.default.bool,
  /**
   * Enables circular(continuous loop) mode, which connects first/last panel for continuous scrolling
   */
  circular: _propTypes.default.bool,
  /**
   * Set panel control mode for the case when circular cannot be enabled.
   * 
   * - "linear" will set the view's range from the top of the first panel to the top of the last panel.
   * - "bound" will prevent the view from going out of the first/last panel, so it won't show empty spaces before/after the first/last panel
   */
  circularFallback: _propTypes.default.oneOf(['linear', 'bound']),
  /**
   * Prevent the view(camera element) from going out of the first/last panel, so it won't show empty spaces before/after the first/last panel
   * Only can be enabled when circular=false
   */
  bound: _propTypes.default.bool,
  /**
   * Update height of the viewport element after movement same to the height of the panel below. 
   * 
   * This can be only enabled when horizontal=true
   */
  adaptive: _propTypes.default.bool,
  /**
   * A visible number of panels on viewport. 
   * 
   * Enabling this option will automatically resize panel size
   */
  panelsPerView: _propTypes.default.number,
  /**
   * Enabling this option will not change width/height style of the panels if panelsPerView is enabled.
   * 
   * This behavior can be useful in terms of performance when you're manually managing all panel sizes
   */
  noPanelStyleOverride: _propTypes.default.bool,
  /**
   * Enabling this option will automatically call 'resize' when all image/video inside panels are loaded.
   * 
   * This can be useful when you have contents inside Flicking that changes its size when it's loaded
   */
  resizeOnContentsReady: _propTypes.default.bool,
  /**
   * If you enable this option on child Flicking when the Flicking is placed inside the Flicking,
   * the parent Flicking will move in the same direction after the child Flicking reaches the first/last panel.
   * 
   * If the parent Flicking and child Flicking have different horizontal option, you do not need to set this option
   */
  nested: _propTypes.default.bool,
  // --- Event ---
  /**
   * A Threshold from viewport edge before triggering 'needPanel' event
   */
  needPanelThreshold: _propTypes.default.number,
  /**
   * When enabled, events are not triggered before ready when initializing
   */
  preventEventsBeforeInit: _propTypes.default.bool,
  // --- Animation ---
  /**
   * Deceleration value for panel movement animation which is triggered by user input. A higher value means a shorter animation time
   */
  deceleration: _propTypes.default.number,
  /**
   * An easing function applied to the panel movement animation
   */
  easing: _propTypes.default.func,
  // --- Input ---
  /**
   * Default duration of the animation (ms)
   */
  duration: _propTypes.default.number,
  /**
   * Types of input devices to enable
   */
  inputType: _propTypes.default.arrayOf(_propTypes.default.oneOf(['touch', 'mouse', 'pointer'])),
  /**
   * Movement style by user input
   */
  moveType: _propTypes.default.oneOf(['snap', 'freeScroll', 'strict']),
  /**
   * Movement threshold to change panel (unit: px). It should be dragged above the threshold to change the current panel
   */
  threshold: _propTypes.default.number,
  /**
   * Set animation to be interruptable by click/touch
   */
  interruptable: _propTypes.default.bool,
  /**
   * The size value of the bounce area. Only can be enabled when circular=false.
   * 
   * You can set different bounce value for prev/next direction by using array. number for px value, and string for px, and % value relative to viewport size.
   * 
   * You have to call Control#updateInput after changing this to take effect
   */
  bounce: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Size of the area from the right edge in iOS safari (in px) which enables swipe-back or swipe-forward
   */
  iOSEdgeSwipeThreshold: _propTypes.default.number,
  /**
   * Automatically prevent click event if the user has dragged at least a single pixel on the viewport element
   */
  preventClickOnDrag: _propTypes.default.bool,
  /**
   * Whether to use the preventDefault when the user starts dragging
   */
  preventDefaultOnDrag: _propTypes.default.bool,
  /**
   * Automatically call disableInput() on initialization
   */
  disableOnInit: _propTypes.default.bool,
  /**
   * Change active panel index on mouse/touch hold while animating. index of the willChange/willRestore event will be used as new index.
   * 
   * This can prevent willRestore from being called on continuous swipe when using circular: true.
   * 
   * It can also create more smooth swipe experience when using moveType: "strict
   */
  changeOnHold: _propTypes.default.bool,
  // --- Performance ---
  /**
   * Whether to render visible panels only.
   * 
   * This can dramatically increase performance when there're many panel
   */
  renderOnlyVisible: _propTypes.default.bool,
  /**
   * By enabling this option, it will reduce memory consumption by restricting the number of DOM elements to panelsPerView + 1
   * 
   * Must be used with 'panelsPerView'. After Flicking's initialized, this property can be used to add/remove the panel count
   */
  virtual: _propTypes.default.bool,
  // --- Others ---
  /**
   * Call init() automatically when creating Flicking's instance
   */
  autoInit: _propTypes.default.bool,
  /**
   * Whether to automatically call resize() when the viewport element(.flicking-viewport)'s size is change
   */
  autoResize: _propTypes.default.bool,
  /**
   * Whether to listen ResizeObserver's event instead of Window's resize event when using the autoResize option.
   * 
   * Enabling this option will make Flicking to resize only when the viewport element(.flicking-viewport)'s size changes
   */
  useResizeObserver: _propTypes.default.bool,
  /**
   * Delays size recalculation from autoResize by the given time in milliseconds.
   * 
   * If the size is changed again while being delayed, it cancels the previous one and delays from the beginning again.
   * 
   * This can increase performance by preventing resize being called too often
   */
  resizeDebounce: _propTypes.default.number,
  /**
   * The maximum time for size recalculation delay when using resizeDebounce, in milliseconds.
   * 
   * This guarantees that size recalculation is performed at least once every (n)ms.
   * 
   * Value should be greater than resizeDebounce
   */
  maxResizeDebounce: _propTypes.default.number,
  /**
   * By enabling this, Flicking will calculate all internal size with CSS width computed with getComputedStyle.
   * 
   * This can prevent 1px offset issue in some cases where panel size has the fractional part.
   * All sizes will have the original size before CSS transform is applied on the element
   */
  useFractionalSize: _propTypes.default.bool
};
Carousel.defaultProps = {
  arrows: false,
  pagination: false,
  paginationType: 'bullet',
  align: 'prev',
  defaultIndex: 0,
  horizontal: true,
  circular: false,
  circularFallback: 'linear',
  bound: false,
  adaptive: false,
  panelsPerView: -1,
  noPanelStyleOverride: false,
  resizeOnContentsReady: false,
  nested: false,
  needPanelThreshold: 0,
  preventEventsBeforeInit: true,
  deceleration: 0.0075,
  easing: x => 1 - Math.pow(1 - x, 3),
  // easeOutCubic
  duration: 500,
  inputType: ['touch', 'mouse'],
  moveType: 'snap',
  threshold: 40,
  interruptable: true,
  bounce: '20%',
  iOSEdgeSwipeThreshold: 30,
  preventClickOnDrag: true,
  preventDefaultOnDrag: false,
  disableOnInit: false,
  changeOnHold: false,
  renderOnlyVisible: false,
  virtual: false,
  autoInit: true,
  autoResize: true,
  useResizeObserver: true,
  resizeDebounce: 0,
  maxResizeDebounce: 100,
  useFractionalSize: false
};
var _default = exports.default = Carousel;